import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import useAuthStore from '../store/useAuthStore';
import { sanitizeEmail } from '../utils/security';
import axios from '../utils/axios';
import Swal from 'sweetalert2';
import { showLoading, closeLoading, showError } from '../utils/sweetalert';

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get('redirect') || '/';
  const { login, loading } = useAuthStore();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [turnstileToken, setTurnstileToken] = useState('');

  useEffect(() => {
    let widgetId = null;
    const checkAndRender = () => {
      if (window.turnstile) {
        try {
          const container = document.getElementById('turnstile-container');
          if (container && container.innerHTML === '') {
            widgetId = window.turnstile.render('#turnstile-container', {
              sitekey: import.meta.env.VITE_CLOUDFLARE_TURNSTILE_SITE_KEY || '0x4AAAAAADoNV3lCYrASum1X',
              callback: (token) => {
                setTurnstileToken(token);
              },
              'expired-callback': () => {
                setTurnstileToken('');
              },
              'error-callback': () => {
                setTurnstileToken('');
              }
            });
          }
        } catch (e) {
          console.error("Turnstile rendering error:", e);
        }
      } else {
        setTimeout(checkAndRender, 100);
      }
    };
    
    checkAndRender();

    return () => {
      if (widgetId !== null && window.turnstile) {
        try {
          window.turnstile.remove(widgetId);
        } catch (e) {
          console.error("Turnstile remove error:", e);
        }
      }
    };
  }, []);

  const handleGoogleCredentialResponse = async (response) => {
    try {
      showLoading(t('auth.login.submitting'));
      const { data } = await axios.post('/auth/google-login', {
        idToken: response.credential
      });

      useAuthStore.getState().loginSuccess(data.data);
      closeLoading();
      toast.success(t('auth.login.success'));
      navigate(redirectUrl);
    } catch (error) {
      closeLoading();
      showError(error.response?.data?.message || error.message || t('auth.login.error'));
    }
  };

  useEffect(() => {
    let checkInterval = null;
    const initializeGoogleSignIn = () => {
      if (window.google && window.google.accounts) {
        clearInterval(checkInterval);
        try {
          window.google.accounts.id.initialize({
            client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID || '1028210564641-ukalb3c67puosiifejcdfki25rltoeum.apps.googleusercontent.com',
            callback: handleGoogleCredentialResponse,
          });
          
          const btnContainer = document.getElementById("google-signin-btn");
          if (btnContainer) {
            const parentWidth = btnContainer.parentElement?.clientWidth || 360;
            const buttonWidth = Math.min(Math.max(parentWidth, 200), 400);

            window.google.accounts.id.renderButton(
              btnContainer,
              { 
                theme: "outline", 
                size: "large", 
                width: Math.floor(buttonWidth),
                height: 44, // Giriş Yap butonu yüksekliğiyle eşitlemek için
                shape: "rectangular" 
              }
            );
          }
        } catch (e) {
          console.error("Google accounts.id render error:", e);
        }
      }
    };

    checkInterval = setInterval(initializeGoogleSignIn, 100);
    return () => clearInterval(checkInterval);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const sanitizedData = {
      email: sanitizeEmail(formData.email),
      password: formData.password,
    };

    if (!sanitizedData.email) {
      toast.error(t('auth.login.validation.email'));
      return;
    }

    if (!sanitizedData.password || sanitizedData.password.length < 6) {
      toast.error(t('auth.login.validation.password'));
      return;
    }

    if (!turnstileToken) {
      toast.error(t('auth.login.validation.captcha'));
      return;
    }

    try {
      const captchaResult = await axios.post('/captcha/verify', {
        token: turnstileToken,
      });

      if (!captchaResult.data.success) {
        toast.error(t('auth.captcha.verifyError'));
        if (window.turnstile) {
          window.turnstile.reset();
        }
        setTurnstileToken('');
        return;
      }

      const result = await login(sanitizedData);

      if (result && result.twoFactorRequired) {
        toast.success(t('auth.2fa.sent'));
        navigate('/verify-2fa', {
          state: {
            email: sanitizedData.email,
            redirect: redirectUrl
          }
        });
        return;
      }

      toast.success(t('auth.login.success'));
      navigate(redirectUrl);
    } catch (error) {
      toast.error(error.message || t('auth.login.error'));
      if (window.turnstile) {
        window.turnstile.reset();
      }
      setTurnstileToken('');
    }
  };



  return (
    <div className="min-h-screen flex items-center justify-center py-16 px-6 bg-champagne-50">
      <div className="max-w-md w-full">
        <div className="text-center mb-12">
          <h2 className="font-serif text-4xl font-light tracking-wide text-gray-900 mb-4">
            {t('auth.login.title')}
          </h2>
          <p className="text-gray-600 font-light tracking-wide">
            Mashow Lüks Takı & Mücevherat
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white p-8 md:p-12 space-y-6 shadow-sm border border-gray-100">
          <div>
            <label htmlFor="email" className="block text-sm tracking-wider uppercase font-light text-gray-900 mb-2">
              {t('auth.login.emailLabel')}
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleChange}
              className="input-field"
              placeholder={t('footer.emailPlaceholder')}
              maxLength="100"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label htmlFor="password" className="block text-sm tracking-wider uppercase font-light text-gray-900">
                {t('auth.login.passwordLabel')}
              </label>
              <Link
                to="/forgot-password"
                className="text-xs text-gray-600 hover:text-gray-900 transition font-light tracking-wide"
              >
                {t('auth.login.forgotPassword')}
              </Link>
            </div>
            <input
              id="password"
              name="password"
              type="password"
              required
              value={formData.password}
              onChange={handleChange}
              className="input-field"
              placeholder="••••••••"
              minLength="6"
              maxLength="100"
            />
          </div>

          {/* Cloudflare Turnstile */}
          <div className="border-t border-gray-100 pt-6 flex flex-col items-center justify-center">
            <label className="block text-sm tracking-wider uppercase font-light text-gray-900 mb-3 text-center w-full">
              {t('auth.captcha.label')}
            </label>
            <div id="turnstile-container" className="min-h-[65px] flex items-center justify-center w-full"></div>
          </div>

          <button
            type="submit"
            disabled={loading || !turnstileToken}
            className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? t('auth.login.submitting') : t('auth.login.submit')}
          </button>

          {/* VEYA Divider */}
          <div className="relative flex items-center justify-center py-2">
            <div className="border-t border-gray-200 w-full"></div>
            <div className="absolute bg-white px-4 text-xs text-gray-400 uppercase tracking-widest font-light">
              {t('auth.login.or')}
            </div>
          </div>

          {/* Hızlı Giriş (Sosyal Giriş) */}
          <div className="space-y-3">
            <div className="text-center text-xs tracking-wider uppercase font-light text-gray-500 mb-2">
              {t('auth.login.socialLoginTitle')}
            </div>
            <div className="w-full flex justify-center h-[44px]">
              <div id="google-signin-btn" className="w-full flex justify-center min-h-[44px]"></div>
            </div>
          </div>

          {/* Kayıt Ol Butonu - Yüksek Görünürlük */}
          <div className="pt-8 border-t border-gray-100 text-center space-y-4">
            <p className="text-xs text-gray-500 font-light tracking-wide uppercase">
              {t('auth.login.noAccountYet')}
            </p>
            <Link
              to={`/register${redirectUrl !== '/' ? `?redirect=${encodeURIComponent(redirectUrl)}` : ''}`}
              className="inline-block w-full text-center py-3.5 border border-gray-950 text-gray-950 font-light tracking-widest uppercase hover:bg-gray-950 hover:text-white transition duration-300 text-sm"
            >
              {t('auth.register.submit')}
            </Link>
          </div>

          <div className="text-center pt-2">
            <Link
              to="/"
              className="text-gray-600 hover:text-gray-900 transition text-sm font-light tracking-wide"
            >
              {t('auth.login.continueShopping')}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
